import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDemandMetrics,
  getDiscomDrawalScheduleMetrics,
  getTotalDrawalScheduleMetrics,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { lightenColor } from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh"];

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

const TYPE = ["Schedule", "Drawal"];

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
}

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        console.error("Invalid date range");
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const rvpnData = await getRajasthanDemandMetrics(
        startDateStr,
        endDateStr
      );
      const discomMetrics = await getDiscomDrawalScheduleMetrics(
        startDateStr,
        endDateStr
      );
      const totalDiscomMetrics = await getTotalDrawalScheduleMetrics(
        startDateStr,
        endDateStr
      );

      setSummaryData(Array.isArray(rvpnData) ? rvpnData : []);
      setDiscomData(Array.isArray(discomMetrics) ? discomMetrics : []);
      setTotalDiscomData(
        Array.isArray(totalDiscomMetrics) ? totalDiscomMetrics : []
      );
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setDiscomData([]);
      setTotalDiscomData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const conversionFactor = selectedUnit === "MWh" ? 0.25 : 1;

    if (selectedGroupKeys === "RVPN Drawal") {
      const processedData = summaryData.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,
        min_demand: item.min_demand * conversionFactor,
        max_demand: item.max_demand * conversionFactor,
        avg_demand: item.avg_demand * conversionFactor,
        mode_demand_window_formula:
          item.mode_demand_window_formula * conversionFactor,
        date_of_min: item.date_of_min,
        date_of_max: item.date_of_max,
      }));

      setChartData(processedData);
      setTableData(processedData);
      setChartKeys([]);
    } else if (selectedGroupKeys === "Total Discom Load") {
      // Use totalDiscomData directly from the API
      const processedData = totalDiscomData.map((item) => ({
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time:
          item.slot && item.time_string
            ? `${item.slot} (${item.time_string})`
            : item.slot,

        min_drawal: (item.min_drawal || 0) * conversionFactor,
        max_drawal: (item.max_drawal || 0) * conversionFactor,
        avg_drawal: (item.avg_drawal || 0) * conversionFactor,
        mode_drawal_window_formula:
          (item.mode_drawal_window_formula || 0) * conversionFactor,

        min_schedule: (item.min_schedule || 0) * conversionFactor,
        max_schedule: (item.max_schedule || 0) * conversionFactor,
        avg_schedule: (item.avg_schedule || 0) * conversionFactor,
        mode_schedule_window_formula:
          (item.mode_schedule_window_formula || 0) * conversionFactor,

        date_of_min_drawal: item.date_of_min_drawal,
        date_of_max_drawal: item.date_of_max_drawal,
        date_of_min_schedule: item.date_of_min_schedule,
        date_of_max_schedule: item.date_of_max_schedule,
      }));

      processedData.sort((a, b) => a.slot - b.slot);

      setChartData(processedData);
      setTableData(processedData);
      setChartKeys([]);
    } else {
      // Discom Load logic remains the same
      let filteredDiscomData = discomData;

      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredDiscomData = filteredDiscomData.filter((item) =>
          selectedDiscom.includes(item.discom)
        );
      }

      const slotMap = new Map<number, any>();

      filteredDiscomData.forEach((item) => {
        const slot = item.slot;
        const discom = item.discom;

        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot: slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }

        const slotData = slotMap.get(slot);

        slotData[`${discom}_min_drawal`] = item.min_drawal * conversionFactor;
        slotData[`${discom}_max_drawal`] = item.max_drawal * conversionFactor;
        slotData[`${discom}_avg_drawal`] = item.avg_drawal * conversionFactor;
        slotData[`${discom}_mode_drawal_window_formula`] =
          item.mode_drawal_window_formula * conversionFactor;

        slotData[`${discom}_min_schedule`] =
          item.min_schedule * conversionFactor;
        slotData[`${discom}_max_schedule`] =
          item.max_schedule * conversionFactor;
        slotData[`${discom}_avg_schedule`] =
          item.avg_schedule * conversionFactor;
        slotData[`${discom}_mode_schedule_window_formula`] =
          item.mode_schedule_window_formula * conversionFactor;

        slotData[`${discom}_date_of_min_drawal`] = item.date_of_min_drawal;
        slotData[`${discom}_date_of_max_drawal`] = item.date_of_max_drawal;
        slotData[`${discom}_date_of_min_schedule`] = item.date_of_min_schedule;
        slotData[`${discom}_date_of_max_schedule`] = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values());

      processedData.sort((a, b) => a.slot - b.slot);

      setChartData(processedData);
      setTableData(processedData);

      const uniqueDiscoms = Array.from(
        new Set(filteredDiscomData.map((item) => item.discom))
      ).sort();
      setChartKeys(uniqueDiscoms);
    }
  }, [
    summaryData,
    discomData,
    totalDiscomData,
    selectedUnit,
    selectedGroupKeys,
    selectedDiscom,
  ]);

  const getLegends = () => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return [
        {
          dataKey: "min_demand",
          name: "Min Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "max_demand",
          name: "Max Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "avg_demand",
          name: "Average Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "mode_demand_window_formula",
          name: "Mode Demand",
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
      ];
    } else if (selectedGroupKeys === "Total Discom Load") {
      // Total Discom Load legends
      const legends: any[] = [];

      selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
        if (suffix === "schedule") {
          legends.push(
            {
              dataKey: "min_schedule",
              name: "Min Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "max_schedule",
              name: "Max Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "avg_schedule",
              name: "Average Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_schedule_window_formula",
              name: "Mode Schedule",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            }
          );
        }

        if (suffix === "drawal") {
          legends.push(
            {
              dataKey: "min_drawal",
              name: "Min Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "max_drawal",
              name: "Max Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "avg_drawal",
              name: "Average Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            },
            {
              dataKey: "mode_drawal_window_formula",
              name: "Mode Drawal",
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            }
          );
        }
      });

      return legends;
    } else {
      // Discom Load legends
      const legends: any[] = [];
      const metricTypes = [
        { field: "min", displayName: "Min" },
        { field: "max", displayName: "Max" },
        { field: "avg", displayName: "Average" },
        { field: "mode", displayName: "Mode" },
      ];

      chartKeys.forEach((discom, discomIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          metricTypes.forEach((metric) => {
            const fieldSuffix =
              metric.field === "mode"
                ? `mode_${suffix}_window_formula`
                : `${metric.field}_${suffix}`;
            const dataKey = `${discom}_${fieldSuffix}`;
            const legendName = `${discom} ${metric.displayName} ${displayName}`;

            legends.push({
              dataKey: dataKey,
              name: legendName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            });
          });
        });
      });

      return legends;
    }
  };

  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];

      let title = param.name;
      const isTimeSlot = true;
      if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const dataPoint = chartData[param.dataIndex];

      const sortedItems = [...items].sort((a, b) => {
        const nameA = (a.seriesName || "").toLowerCase();
        const nameB = (b.seriesName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const tooltipItems = sortedItems.map((item) => {
        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;

        let displaySeriesName = item.seriesName;

        if (selectedGroupKeys === "RVPN Drawal") {
          if (item.seriesName === "Min Demand" && dataPoint?.date_of_min) {
            displaySeriesName = `Min Demand (${dataPoint.date_of_min})`;
          } else if (
            item.seriesName === "Max Demand" &&
            dataPoint?.date_of_max
          ) {
            displaySeriesName = `Max Demand (${dataPoint.date_of_max})`;
          }
        } else if (selectedGroupKeys === "Total Discom Load") {
          // Add date tooltips for Total Discom Load
          if (
            item.seriesName === "Min Schedule" &&
            dataPoint?.date_of_min_schedule
          ) {
            displaySeriesName = `Min Schedule (${dataPoint.date_of_min_schedule})`;
          } else if (
            item.seriesName === "Max Schedule" &&
            dataPoint?.date_of_max_schedule
          ) {
            displaySeriesName = `Max Schedule (${dataPoint.date_of_max_schedule})`;
          } else if (
            item.seriesName === "Min Drawal" &&
            dataPoint?.date_of_min_drawal
          ) {
            displaySeriesName = `Min Drawal (${dataPoint.date_of_min_drawal})`;
          } else if (
            item.seriesName === "Max Drawal" &&
            dataPoint?.date_of_max_drawal
          ) {
            displaySeriesName = `Max Drawal (${dataPoint.date_of_max_drawal})`;
          }
        } else if (selectedGroupKeys === "Discom Load") {
          const seriesNameParts = item.seriesName.split(" ");
          const discom = seriesNameParts[0];

          if (item.seriesName.includes("Min Schedule")) {
            const dateField = `${discom}_date_of_min_schedule`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Schedule")) {
            const dateField = `${discom}_date_of_max_schedule`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Min Drawal")) {
            const dateField = `${discom}_date_of_min_drawal`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          } else if (item.seriesName.includes("Max Drawal")) {
            const dateField = `${discom}_date_of_max_drawal`;
            if (dataPoint?.[dateField]) {
              displaySeriesName = `${item.seriesName} (${dataPoint[dateField]})`;
            }
          }
        }

        return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${bgColor}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${selectedUnit}</div>
        </div>
      `;
      });

      const tooltipId = `tooltip-${Date.now()}`;

      return `
      <div class="enhanced-tooltip" id="${tooltipId}">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        
        <div class="tooltip-content">
          ${tooltipItems.join("")}
        </div>
      </div>
      
      <style>
        .enhanced-tooltip {
          font-family: 'Inter', system-ui, sans-serif;
          width: 100%;
          min-width: 200px;
        }
        .tooltip-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: start;
          align-items: start;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .tooltip-title {
          font-weight: 600;
          font-size: 13px;
          color: #333;
        }
        .tooltip-content {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .tooltip-item:last-child {
          border-bottom: none;
        }
        .color-indicator {
          width: 12px;
          height: 12px;
          flex-shrink: 0;
        }
        .series-name {
          font-weight: 500;
          font-size: 12px;
          color: #333;
          flex: 1;
        }
        .value {
          font-weight: 600;
          font-size: 12px;
          color: #333;
        }
      </style>
    `;
    },
    [chartData, selectedUnit, selectedGroupKeys]
  );

  const getExtraKeys = () => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return ["slot_number", "date_of_min", "date_of_max"];
    } else if (selectedGroupKeys === "Total Discom Load") {
      return [
        "slot_number",
        "date_of_min_schedule",
        "date_of_max_schedule",
        "date_of_min_drawal",
        "date_of_max_drawal",
      ];
    } else {
      const baseKeys = ["slot_number"];
      chartKeys.forEach((discom) => {
        baseKeys.push(
          `${discom}_date_of_min_schedule`,
          `${discom}_date_of_max_schedule`,
          `${discom}_date_of_min_drawal`,
          `${discom}_date_of_max_drawal`
        );
      });
      return baseKeys;
    }
  };

  const getExtraLabels = () => {
    if (selectedGroupKeys === "RVPN Drawal") {
      return ["Block", "Min Date", "Max Date"];
    } else if (selectedGroupKeys === "Total Discom Load") {
      return [
        "Block",
        "Min Schedule Date",
        "Max Schedule Date",
        "Min Drawal Date",
        "Max Drawal Date",
      ];
    } else {
      const baseLabels = ["Block"];
      chartKeys.forEach((discom) => {
        baseLabels.push(
          `${discom} Min Schedule Date`,
          `${discom} Max Schedule Date`,
          `${discom} Min Drawal Date`,
          `${discom} Max Drawal Date`
        );
      });
      return baseLabels;
    }
  };

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: getExtraKeys(),
    extraLabels: getExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Rajasthan Load Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Discom"
              items={DISCOM.map((item) => item.label)}
              selectedItems={selectedDiscom}
              onSelect={(item, index) => {}}
              setSelectedItems={(item) => {
                if (selectedGroupKeys === "Total Discom Load") {
                  dispatch(setLoadingFilterName(""));
                  return;
                }
                onDiscomChange(item);
              }}
            />
          )}

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />

          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={TYPE}
              selectedItems={selectedSuffix.map(
                (item) => item?.displayName || ""
              )}
              setSelectedItems={(selected: string[]) => {
                onTypeChange(selected);
              }}
            />
          )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              Rajasthan Load Summary ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              key={`${selectedChartType.value}-${selectedUnit}`}
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey="slot"
              secondXDataKey={undefined}
              customTooltip={customTooltipFormatter}
            />
          )}

          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1.	 Actual Drawal = Restricted Demand
           Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.	 Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.	MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
